/**
 * §8.11 Support Center — 6 support channels with SLA badges.
 */

import React from 'react'
import {
  LifeBuoy, CreditCard, Handshake, Users, BookOpen, Activity, Sparkles, ArrowRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { SUPPORT_CHANNELS } from '../data'
import { TONE } from '../_shared'

const ICONS = { LifeBuoy, CreditCard, Handshake, Users, BookOpen, Activity }

export default function Support() {
  return (
    <section id="support" className="relative py-24 md:py-28 scroll-mt-24">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Support Center</Eyebrow>
          <SectionHeading align="center"
            title={<>Reach{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">the right team.</span></>}
            subtitle="Six channels cover the full spectrum of pre-sales, technical, billing, partnerships and self-serve needs — with response SLAs shown up front."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {SUPPORT_CHANNELS.map((c) => {
            const tone = TONE[c.tone] || TONE.orange
            const Icon = ICONS[c.iconKey] || Sparkles
            return (
              <div key={c.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px] overflow-hidden flex flex-col gap-3">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                <div className="relative flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/25 bg-emerald-400/[0.06] px-2 py-0.5 text-[9.5px] font-mono uppercase tracking-wider text-emerald-400">
                    {c.sla}
                  </span>
                </div>
                <div className="relative">
                  <h4 className="text-[15px] font-semibold text-white leading-tight">{c.label}</h4>
                  <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-1.5">{c.body}</p>
                </div>
                <div className="relative mt-auto pt-1 inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 hover:text-brand-gold transition-colors">
                  Open channel <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
